import { Media } from "../../../../../../vl-api/vlSchemas";
import styles from "./FramesCarousel.module.scss";
import { FrameTimestamp } from "../../../../../uikit/FrameTimestamp/FrameTimestamp";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import ContentBodyLoader from "../../../../../components/MetadataSummary/Content/ContentBody/ContentBodyLoader";
import { VLSegmented } from "../../../../../uikit/VLSegmented/VLSegmented";
import { ResultsIcon, TimelineIcon } from "./Icons";
import { CarouselTitle } from "../CarouselTitle/CarouselTitle";
import RelevanceIcon from "../../../../../uikit/RelevanceIcon";
export type FrameData = Media;

type Mode = "timeline" | "results";

export const FramesCarousel = ({
  frames,
  framesFromSearch,
  height,
  currentImageId,
  onImageChange,
  isContentLoading,
  initialMode,
}: {
  initialMode: Mode;
  isContentLoading: boolean;
  onImageChange: (img: Media) => void;
  currentImageId: string;
  height: number;
  frames: FrameData[];
  framesFromSearch: FrameData[];
}) => {
  const imagesRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<Mode>(initialMode);
  const [isLoaded, setIsLoaded] = useState(false);
  useEffect(() => {
    const element = document.getElementById(currentImageId);
    if (element && isLoaded) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentImageId, isLoaded]);
  useEffect(() => {
    if (imagesRef.current) {
      imagesRef.current.addEventListener("wheel", (e) => {
        if (e.deltaY) {
          e.preventDefault();
          imagesRef.current!.scrollLeft += e.deltaY;
        }
      });
    }
  }, []);
  useEffect(() => {
    setIsLoaded(false);
  }, [frames]);
  useEffect(() => {
    const preloadImages = async () => {
      setIsLoaded(false);
      const imagesUrls = frames.map((f) => f.image_uri);
      await Promise.all(
        imagesUrls.map((url) => {
          return new Promise((resolve) => {
            const img = new Image();
            img.onload = resolve;
            img.src = url!;
          });
        }),
      );
      setIsLoaded(true);
    };
    preloadImages();
  }, [frames]);
  const handleToggleChange = (newMode: Mode) => {
    if (newMode === "timeline") {
      setMode(newMode);
    } else {
      const isFrameExistInSearchResults = framesFromSearch.find(
        (f) => f.image_id === currentImageId,
      );
      if (!isFrameExistInSearchResults) {
        onImageChange(framesFromSearch[0]);
      }
      setMode(newMode);
    }
  };

  const displayedFrames = mode === "timeline" ? frames : framesFromSearch;
  return (
    <div className={styles.container} style={{ height }}>
      <CarouselTitle
        text="Frames"
        count={displayedFrames.length}
        tooltipText={
          mode === "timeline"
            ? "All Extracted Frames"
            : "Frames Matching Search Results"
        }
      >
        {Boolean(framesFromSearch.length) && (
          <VLSegmented
            gradientToggle
            size="small"
            shape="round"
            value={mode}
            onChange={(newMode) => handleToggleChange(newMode as Mode)}
            options={[
              {
                value: "results",
                label: (
                  <div className={styles.toggleItem}>
                    <ResultsIcon /> Results
                  </div>
                ),
              },
              {
                value: "timeline",
                label: (
                  <div className={styles.toggleItem}>
                    <TimelineIcon /> Timeline
                  </div>
                ),
              },
            ]}
          />
        )}
      </CarouselTitle>
      {!frames.length || !isLoaded ? (
        <div className={styles.images}>
          <ContentBodyLoader center size={64} />
        </div>
      ) : (
        <div
          ref={imagesRef}
          className={classNames(styles.images, styles.loaded, {
            [styles.disabled]: isContentLoading,
          })}
        >
          {displayedFrames.map((frame) => (
            <div
              key={frame.image_id!}
              id={frame.image_id!}
              className={styles.imageContainer}
              onClick={() => onImageChange(frame)}
            >
              {mode === "results" && (
                <RelevanceIcon
                  relevance={{
                    zone: frame.relevance_zone!,
                    score: frame.relevance_score!,
                  }}
                />
              )}
              <FrameTimestamp timestamp={frame.frame_timestamp!} small />
              <img
                className={classNames(styles.image, {
                  [styles.active]: frame.image_id === currentImageId,
                })}
                src={frame.image_uri!}
                alt={frame.file_name!}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
